<script lang="jsx">
import { isArray, isBoolean, isEmpty, isFunction } from '@/utils/is';

let rawModel = new WeakMap();

export default {
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Array,
      default: () => []
    },
    model: {
      type: Object,
      default: () => ({})
    },
    operations: {
      type: Object,
      default: () => ({})
    },
    dataSource: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    isShow(e) {
      if (!('show' in e)) return true;

      return isFunction(e.show) ? e.show(this.model[e.prop], this.model, e) : e.show;
    },
    isDisable(props) {
      const disabled = isFunction(props.disabled) ? props.disabled(this.model) : props.disabled;

      return isEmpty(disabled) ? false : disabled;
    },
    validate(callback) {
      return this.$refs.form.validate(callback);
    },
    validateField(field, callback) {
      return this.$refs.form.validateField(field, callback);
    },
    genInput(config, model) {
      return (
        <el-input
          props={config.componentProps || {}}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-input>
      );
    },
    genSelect(config, model) {
      const { componentProps = {}, componentAttrs = {}, dataKey } = config || {};

      const { options, props = { label: 'label', value: 'value' }, ...compoProps } = componentProps;

      const opts = options || this.dataSource[dataKey] || [];

      const optVNode = opts.map(e => (
        <el-option label={e[props.label]} value={e[props.value]} key={e[props.value]}></el-option>
      ));

      return (
        <el-select
          props={compoProps || {}}
          attrs={componentAttrs}
          disabled={this.isDisable(componentProps)}
          v-model={model[config.prop]}
        >
          {optVNode}
        </el-select>
      );
    },
    genRadio(config, model) {
      const { componentProps = {}, dataKey, on = {}, attrs = {} } = config || {};

      let {
        props = { label: 'label', value: 'value' },
        options = this.dataSource[dataKey],
        ...compoProps
      } = componentProps;

      const nodes = options.map(e => {
        return (
          <el-radio label={e[props.value]} disabled={this.isDisable(e || {})}>
            {e[props.label]}
          </el-radio>
        );
      });

      return (
        <el-radio-group props={compoProps} v-model={model[config.prop]} on={on} attrs={attrs}>
          {nodes}
        </el-radio-group>
      );
    },
    genCheckboxGroup(config, model) {
      const { componentProps = {}, dataKey } = config || {};

      let {
        props = { label: 'label', value: 'value' },
        options = this.dataSource[dataKey],
        ...compoProps
      } = componentProps;

      const nodes = options.map(e => {
        const indeterminate = isFunction(e.indeterminate)
          ? e.indeterminate(e, model)
          : isBoolean(e.indeterminate)
          ? e.indeterminate
          : undefined;

        return (
          <el-checkbox
            label={e[props.value]}
            indeterminate={indeterminate}
            disabled={this.isDisable(e || {})}
          >
            {e[props.label]}
          </el-checkbox>
        );
      });

      return (
        <el-checkbox-group props={compoProps || {}} v-model={model[config.prop]}>
          {nodes}
        </el-checkbox-group>
      );
    },
    genCheckbox(config, model) {
      const { componentProps = {}, on = {} } = config || {};
      let { label, indeterminate, ...compoProps } = componentProps;

      indeterminate = isFunction(indeterminate) ? indeterminate(model) : indeterminate;

      return (
        <el-checkbox
          props={compoProps}
          on={on}
          indeterminate={indeterminate}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        >
          {label}
        </el-checkbox>
      );
    },
    genDate(config, model) {
      return (
        <el-date-picker
          props={config.componentProps || {}}
          attrs={config.componentAttrs || {}}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-date-picker>
      );
    },
    genTime(config, model) {
      return (
        <el-time-picker
          props={config.componentProps || {}}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-time-picker>
      );
    },
    genDatetime(config, model) {
      return (
        <el-date-picker
          props={config.componentProps || {}}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-date-picker>
      );
    },
    genUpload(config, model) {
      return (
        <el-upload
          props={config.componentProps || {}}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-upload>
      );
    },
    genTextarea(config, model) {
      return (
        <el-input
          props={config.componentProps || {}}
          type="textarea"
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-input>
      );
    },
    genCascader(config, model) {
      return (
        <el-cascader
          props={config.componentProps || {}}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-cascader>
      );
    },
    genSlider(config, model) {
      return (
        <el-slider props={config.componentProps || {}} v-model={model[config.prop]}></el-slider>
      );
    },
    genRate(config, model) {
      return <el-rate props={config.componentProps || {}} v-model={model[config.prop]}></el-rate>;
    },
    genSwitch(config, model) {
      return (
        <el-switch
          props={config.componentProps || {}}
          disabled={this.isDisable(config.componentProps || {})}
          v-model={model[config.prop]}
        ></el-switch>
      );
    },
    genText(config) {
      return <span attrs={config.componentAttrs || {}}>{config.text}</span>;
    },
    genButton(config, model, parentModel) {
      const { componentProps } = config || {};

      const { onClick, ...props } = componentProps || {};

      const handleClick = event => {
        onClick?.(model, parentModel, event);
      };

      return (
        <el-button
          props={props}
          disabled={this.isDisable(componentProps || {})}
          onClick={handleClick}
        >
          {config.text}
        </el-button>
      );
    },
    genDivider(config) {
      return <el-divider props={config.componentProps} />;
    },
    genGroup(config, model) {
      const { prop, groupProps } = config;
      const { title, rowProps, slots = {}, cardProps, options, operations } = groupProps;

      const groupModel = model[prop];

      const getDef = (model, parentModel) =>
        this.genContent(rowProps, model, options, operations, parentModel);

      const defSlots = {
        header: () => <div>{title}</div>,
        default: () => {
          if (isArray(groupModel)) {
            return groupModel.map(e => getDef(e, groupModel));
          } else {
            return getDef(groupModel, model);
          }
        }
      };

      const $slots = { ...defSlots, ...slots };

      const _slots = {
        header: () => $slots.header(groupModel, model),
        default: () => $slots.default(groupModel, model)
      };

      if ($slots.footer) {
        _slots.footer = () => $slots.footer(groupModel, model);
      }

      return <el-card props={cardProps}>{_slots}</el-card>;
    },
    resetForm() {
      Object.assign(this.model, JSON.parse(rawModel.get(this)));
    },
    genOperations(operations) {
      if (isEmpty(operations)) return null;

      const { attrs, buttonAttrs } = operations;

      return (
        <div class="flex gap-1 operations-wrap" attrs={attrs}>
          {operations.list.map(e => {
            return (
              <el-button
                attrs={e.buttonAttrs || buttonAttrs}
                loading={e.loading}
                disabled={this.isDisable(e)}
                onClick={() => e.onClick(this.model, e, this)}
                props={e.buttonProps}
              >
                {e.text}
              </el-button>
            );
          })}
        </div>
      );
    },
    genComponent(e, model, parentModel) {
      const dict = {
        input: this.genInput,
        select: this.genSelect,
        radio: this.genRadio,
        checkbox: this.genCheckbox,
        checkboxGroup: this.genCheckboxGroup,
        datePicker: this.genDate,
        time: this.genTime,
        datetime: this.genDatetime,
        upload: this.genUpload,
        textarea: this.genTextarea,
        cascader: this.genCascader,
        slider: this.genSlider,
        text: this.genText,
        button: this.genButton,
        group: this.genGroup,
        divider: this.genDivider
      };

      return dict[e.type](e, model, parentModel);
    },
    genFormItem(e, model, parentModel) {
      const colProps = e.colProps || this.config.colProps || {};

      const formItemAttrs = e.formItemAttrs || this.config.formItemAttrs || {};

      return this.genCol(
        colProps,
        <el-form-item label={e.label} prop={e.prop} props={e.formItemProps} attrs={formItemAttrs}>
          {this.genComponent(e, model, parentModel)}
        </el-form-item>
      );
    },
    genCol(colProps, nodes) {
      if (!this.config.useRow) return nodes;

      return <el-col props={colProps}>{nodes}</el-col>;
    },
    genRow(props, nodes) {
      if (!this.config.useRow) return nodes;

      return <el-row props={props}>{nodes}</el-row>;
    },
    genContent(rowProps, model, options, operations, parentModel) {
      const operationsVM = !isEmpty(operations)
        ? this.genCol(operations.colProps, this.genOperations(operations))
        : null;

      return this.genRow(rowProps, [
        options.map(e => {
          return this.isShow(e) ? this.genFormItem(e, model, parentModel) : null;
        }),
        operationsVM
      ]);
    }
  },
  render() {
    const { config, options, model, operations } = this;

    const { rowProps = {}, formProps = {}, formAttrs = {} } = config || {};

    if (isEmpty(rawModel.get(this))) rawModel.set(this, JSON.stringify(this.model));

    const content = this.genContent(rowProps, model, options, operations);

    return (
      <el-form ref="form" class="custom-form" props={{ ...formProps, model }} attrs={formAttrs}>
        {content}
      </el-form>
    );
  }
};
</script>

<style lang="scss">
.custom-form {
  .el-date-editor {
    --el-date-editor-width: 100%;
  }

  .flex {
    display: flex;
  }

  .gap-1 {
    gap: 1em;
  }

  .gap-2 {
    gap: 2em;
  }

  .operations-wrap {
    .el-button {
      margin-left: 0 !important;
    }
  }

  .el-card {
    --el-card-padding: 10px;

    width: 100%;
    margin-top: 20px;

    .el-form:last-of-type {
      .el-row:last-of-type {
        .el-col {
          .el-divider {
            display: none;
          }
        }
      }
    }
  }
}
</style>
